"use client";

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { SOCIAL_PROOF } from "@/lib/marketing/copy";
import { testimonialsAvatars } from "@/lib/marketing/images";
import { SectionHeader } from "@/components/common/SectionHeader";
import { cn } from "@/lib/utils";

export function TestimonialsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const testimonials = SOCIAL_PROOF.testimonials;

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (!isMobile) return;
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [isMobile, next]);

  return (
    <section className="zaklyn-section bg-secondary/30">
      <div className="zaklyn-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-8 md:gap-16 mb-16"
        >
          {SOCIAL_PROOF.stats.map((stat, index) => (
            <div key={stat.label} className="text-center">
              <div className="font-display text-4xl md:text-5xl font-semibold text-foreground">
                {stat.value}
                <span className="text-primary">{stat.suffix}</span>
              </div>
              <div className="mt-2 text-sm text-muted-foreground font-body">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        <SectionHeader
          title={SOCIAL_PROOF.testimonialsTitle}
        />

        {isMobile ? (
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-2xl p-6 shadow-zaklyn border border-border/50"
              >
                <Quote className="h-8 w-8 text-primary/20 mb-4" />
                <p className="text-foreground font-body text-lg leading-relaxed">
                  "{testimonials[currentIndex].quote}"
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-rose-gold/20" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">
                      {testimonials[currentIndex].name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {testimonials[currentIndex].location}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-1">
                  {Array.from({ length: testimonials[currentIndex].rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                onClick={prev}
                className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-zaklyn border border-border/50 hover:bg-secondary transition-colors"
              >
                <ChevronLeft className="h-5 w-5 text-foreground" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all",
                      index === currentIndex ? "w-6 bg-primary" : "bg-border"
                    )}
                  />
                ))}
              </div>
              <button
                onClick={next}
                className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-zaklyn border border-border/50 hover:bg-secondary transition-colors"
              >
                <ChevronRight className="h-5 w-5 text-foreground" />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-zaklyn border border-border/50"
              >
                <Quote className="h-8 w-8 text-primary/20 mb-4" />
                <p className="text-foreground font-body leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-rose-gold/20" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {testimonial.location}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-1">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
